import { createEvent } from '@utils/domUtils';
import { getJobFormData } from '@utils/jobUtils';
import {
  createCategoryValidations,
  createGeneralValidations,
  createJobAdValidations,
  createPublishChannelsValidations,
  createSummaryValidations,
} from './validations';

/* global KTUtil, KTWizard, Swal */

export default class JobCreateWizard {
  static selectorString() {
    return '.JobCreateWizard';
  }

  getData() {
    return getJobFormData(
      this.form,
      this.jobAdSections.map(section => section.sectionId),
    );
  }

  render(node, props) {
    this.form = node.querySelector('.wizard-form');
    this.jobAdSections = [...node.querySelectorAll('.JobEditSidebar [data-section]')];
    this.jobAdSections.forEach(element => {
      element.sectionId = element.getAttribute('data-section');
    });

    const categoryValidations = createCategoryValidations(this.form);
    const generalValidations = createGeneralValidations(this.form);
    const jobAdValidations = createJobAdValidations(this.form);
    const publishChannelsValidations = createPublishChannelsValidations(this.form);
    const summaryValidations = createSummaryValidations(this.form);

    this.validations = {
      category: categoryValidations,
      general: generalValidations,
      jobAd: jobAdValidations,
      publishChannels: publishChannelsValidations,
      summary: summaryValidations,
    };

    this.steps = [...node.querySelectorAll('[data-step]')].map(element => {
      element.stepId = element.getAttribute('data-step');
      return element;
    });

    const wizardNode = node.querySelector('.wizard');
    const startStepIndex = Number(wizardNode.getAttribute('data-active-step'));

    this.wizardController = new KTWizard(wizardNode, {
      startStep: startStepIndex + 1,
      clickableSteps: true,
    });

    this.steps.forEach((step, index) =>
      startStepIndex > index ? step.setAttribute('data-validated', 'true') : null,
    );

    // Validate current step
    this.wizardController.on('change', () => {
      this.goToStep(this.wizardController.getNewStep() - 1);
      return false;
    });

    // Scroll to top after changing wizard step
    this.wizardController.on('changed', () => {
      KTUtil.scrollTop();
      node.dispatchEvent(createEvent('stepChanged'));
    });

    this.beforeUnload = this.beforeUnload.bind(this);

    this.form.addEventListener('submit', event => {
      // TODO: check if file uploads are still running, and if so: render alert message and wait with submission...
      // TODO: just check dom for .dz-pending (or similar, verify first) items...

      // NOTE: dirty hack: do not validate summary step
      const allStepsValid =
        this.steps.filter(
          step => step.stepId === 'summary' || step.getAttribute('data-validated') === 'true',
        ).length === this.steps.length;

      const isOnLastStep = this.wizardController.getStep() === this.steps.length;

      if (!(allStepsValid && isOnLastStep)) {
        event.preventDefault();
        return false;
      } else {
        this.form.setAttribute('data-all-validated', 'true');
        window.removeEventListener('beforeunload', this.beforeUnload);
      }
    });

    window.addEventListener('beforeunload', this.beforeUnload);
  }

  beforeUnload(event) {
    if (this.form.getAttribute('data-all-validated') === 'true') {
      return;
    }

    event.preventDefault();

    event.returnValue =
      'Sind Sie sicher, dass Sie diese Seite verlassen möchten? Ihr Arbeitsfortschritt geht dadurch verloren';

    return event.returnValue;
  }

  goToStep(targetStepIndex) {
    const currentStepIndex = this.wizardController.getStep() - 1;

    // skip validation if going back a step
    if (currentStepIndex > targetStepIndex) {
      this.wizardController.goTo(targetStepIndex + 1);
      return;
    }

    this.validateFormStep(currentStepIndex, targetStepIndex);
    this.node.dispatchEvent(createEvent('stepChange'));
  }

  async getStepValidationStatus(validator) {
    const status = await validator.validate();
    return status === 'Valid';
  }

  async validateFormStep(currentStepIndex, targetStepIndex) {
    const lastValidStep = await this.validateStep(targetStepIndex, currentStepIndex);
    this.wizardController.goTo(lastValidStep + 1);
    KTUtil.scrollTop();
  }

  async validateStep(targetStepIndex, stepIndex) {
    // reached target step: don't validate that step but instead report that validation was successful
    if (stepIndex === targetStepIndex) {
      return stepIndex;
    }

    const step = this.steps[stepIndex];
    const validator = this.validations[step.stepId];

    // NOTE: add attribute `data-tried-validating="true"` to step
    // so that validator can show error messages only after tried to validate before
    step.setAttribute('data-tried-validating', 'true');

    // assume step is valid if it doesn't have a validator
    const valid = validator ? await this.getStepValidationStatus(validator) : true;

    if (valid) {
      step.setAttribute('data-validated', 'true');
      return await this.validateStep(targetStepIndex, stepIndex + 1);
    }

    step.removeAttribute('data-validated');

    // TODO: adjust sweet alert messages, define texts in individual steps and / or job create wizard component as prop
    await Swal.fire({
      text: 'Die eingegebenen Daten sind unvollständig. Bitte überprüfen Sie Ihre Angaben.',
      icon: 'error',
      buttonsStyling: false,
      confirmButtonText: 'Verstanden',
      customClass: {
        confirmButton: 'btn font-weight-bold btn-light',
      },
    });

    return stepIndex;
  }
}
